import { Clock, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import type { RankingNotice } from '../ranking-notices';
import styles from './RankingNotices.module.css';

// ADR 0062, mockup M2: one banner per notice. The wording is `noticesOf`'s; this only draws it.

const READ_ONLY_TITLE = 'You can see this job’s ranking but can’t change it.';

interface RankingNoticesProps {
  readonly notices: readonly RankingNotice[];
  readonly canRank: boolean;
  readonly isRanking: boolean;
  readonly onRank: () => void;
}

export function RankingNotices({ notices, canRank, isRanking, onRank }: RankingNoticesProps): ReactNode {
  return (
    <>
      {notices.map((notice) => {
        const Icon = notice.tone === 'warn' || notice.kind === 'old_order' ? Info : Clock;
        const share = notice.progress && notice.progress.total > 0 ? Math.min(1, notice.progress.done / notice.progress.total) : 0;
        return (
          <section key={notice.kind} className={styles.notice} data-tone={notice.tone} data-kind={notice.kind} role="status">
            <span className={styles.icon}>
              <Icon size={16} aria-hidden />
            </span>
            <span className={styles.text}>
              <span className={styles.title}>{notice.title}</span>
              <span className={styles.body}>{notice.body}</span>
            </span>
            {notice.progress && (
              <span className={styles.progress}>
                {notice.progress.done.toLocaleString('en-US')} of {notice.progress.total.toLocaleString('en-US')}
                <span className={styles.track} aria-hidden>
                  <span className={styles.fill} style={{ inlineSize: `${Math.round(share * 100)}%` }} />
                </span>
              </span>
            )}
            {notice.action && (
              <Button variant="solid" size="sm" isPending={isRanking} disabled={!canRank} title={canRank ? undefined : READ_ONLY_TITLE} onClick={onRank}>
                {notice.action.label}
              </Button>
            )}
          </section>
        );
      })}
    </>
  );
}
